import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { makeStyles, useTheme } from "@seahorse/ui";
import {
  formatChartAxisTime,
  formatShortTimeWithZone,
  getHighchartsTimeOptions,
  getTimeZoneSuffix,
  parseUtcTimestampToMs,
} from "../../Utils/timezone";
import { useDisplayTimeZone } from "../../Utils/displayTimeZone";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import Boost from "highcharts/modules/boost";
import { getAuthenticatedWebSocketUrl } from "../../Utils/CoTraderUtils";

Boost(Highcharts);

type FastForexPricePoint = {
  timestamp: string;
  originalDate: number;
  spot_bid: number;
  spot_midpoint: number;
  spot_ask: number;
  current_price: number;
};

const FAST_FOREX_INTERVAL = "PT1M";
const FAST_FOREX_MAX_POINTS = 100;
const DEFAULT_PRICE_WINDOW_MS = 60 * 60 * 1000;
const LIVE_TICKS_PER_HISTORY_POINT = 60;
const LIVE_CHART_MAX_POINTS = 60;

const toFiniteNumber = (value: unknown) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const formatFastForexUtcTimestamp = (timestampMs: number) =>
  new Date(Math.floor(timestampMs / 1000) * 1000)
    .toISOString()
    .replace(".000Z", "Z");

const normaliseFastForexPrices = (results: unknown): FastForexPricePoint[] => {
  if (!Array.isArray(results)) return [];

  return results
    .map((result): FastForexPricePoint | null => {
      const timestampMs = parseUtcTimestampToMs(result?.timestamp);
      const bid = toFiniteNumber(result?.spot_bid);
      const ask = toFiniteNumber(result?.spot_ask);
      const suppliedMid = toFiniteNumber(result?.spot_midpoint);
      const midpoint =
        suppliedMid ?? (bid != null && ask != null ? (bid + ask) / 2 : null);

      if (
        !Number.isFinite(timestampMs) ||
        bid == null ||
        ask == null ||
        midpoint == null
      ) {
        return null;
      }

      return {
        timestamp: new Date(timestampMs).toISOString(),
        originalDate: toFiniteNumber(result?.originalDate) ?? timestampMs,
        spot_bid: bid,
        spot_midpoint: midpoint,
        spot_ask: ask,
        current_price: midpoint,
      };
    })
    .filter((result): result is FastForexPricePoint => result != null)
    .sort((left, right) => left.originalDate - right.originalDate);
};

const fetchFastForexPrices = async (
  currencyPair: string,
  referenceTimestampMs: number,
  priceWindowMs: number,
  signal: AbortSignal,
  requestedPointCount?: number,
  preferStoredQuotes = false
) => {
  const numberOfPoints = Math.max(
    1,
    Math.min(
      FAST_FOREX_MAX_POINTS,
      requestedPointCount ?? Math.ceil(priceWindowMs / (60 * 1000))
    )
  );
  const params = new URLSearchParams({
    symbol: currencyPair.replace(/\//g, ""),
    startdatetime: formatFastForexUtcTimestamp(
      referenceTimestampMs - priceWindowMs
    ),
    number_of_points: String(numberOfPoints),
    interval: FAST_FOREX_INTERVAL,
  });
  if (preferStoredQuotes) {
    params.set("prefer_stored", "true");
  }
  const apiUrl = process.env.REACT_APP_API_URL?.replace(/\/$/, "");

  if (!apiUrl) {
    throw new Error("The API URL is not configured.");
  }

  const response = await fetch(
    `${apiUrl}/globallink/prices/get_prices_fastforex?${params.toString()}`,
    {
      method: "GET",
      signal,
    }
  );

  if (!response.ok) {
    throw new Error(`FastForex price request failed (${response.status}).`);
  }

  const data = await response.json();
  return normaliseFastForexPrices(data?.results);
};

const normaliseFastForexSymbol = (value: unknown) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, "");

const normaliseFastForexLiveTick = (tick: any): FastForexPricePoint | null => {
  const bid = toFiniteNumber(tick?.bid);
  const ask = toFiniteNumber(tick?.ask);
  const suppliedMid = toFiniteNumber(tick?.price);
  const midpoint =
    suppliedMid ?? (bid != null && ask != null ? (bid + ask) / 2 : null);
  const parsedTimestamp = parseUtcTimestampToMs(tick?.timestamp);
  const timestampMs = Number.isFinite(parsedTimestamp)
    ? parsedTimestamp
    : Date.now();

  if (bid == null || ask == null || midpoint == null) {
    return null;
  }

  return {
    timestamp: new Date(timestampMs).toISOString(),
    originalDate: timestampMs,
    spot_bid: bid,
    spot_midpoint: midpoint,
    spot_ask: ask,
    current_price: midpoint,
  };
};

const addFastForexHistoryPoint = (
  prices: FastForexPricePoint[],
  nextPrice: FastForexPricePoint
) =>
  [...prices, nextPrice]
    .sort((left, right) => left.originalDate - right.originalDate)
    .slice(-LIVE_CHART_MAX_POINTS);

const updateLiveChartPoint = (
  chart: Highcharts.Chart | undefined,
  price: FastForexPricePoint,
  appendPoint: boolean,
  showBidAsk: boolean
) => {
  if (!chart) return false;

  const values = showBidAsk
    ? [price.spot_bid, price.spot_midpoint, price.spot_ask]
    : [price.spot_midpoint];

  if (chart.series.length !== values.length) return false;

  chart.series.forEach((series, index) => {
    const lastPoint = series.data[series.data.length - 1];
    if (appendPoint || !lastPoint) {
      series.addPoint(
        [price.originalDate, values[index]],
        false,
        series.data.length >= LIVE_CHART_MAX_POINTS,
        false
      );
      return;
    }

    lastPoint.update([price.originalDate, values[index]], false, false);
  });

  chart.redraw(false);
  return true;
};

export interface FastForexPriceChartTileComponentProps {
  currencyPair: string;
  executionTimestamp?: string;
  priceWindowMs?: number;
  showBidAsk?: boolean;
  chartHeight?: string | number;
  enableLiveUpdates?: boolean;
  onPriceChange?: (price: number) => void;
  historyPointCount?: number;
  preferStoredQuotes?: boolean;
}

const FastForexPriceChartTileComponent: React.FunctionComponent<
  FastForexPriceChartTileComponentProps
> = (
  {
    currencyPair,
    executionTimestamp,
    priceWindowMs = DEFAULT_PRICE_WINDOW_MS,
    showBidAsk = true,
    chartHeight = "200px",
    enableLiveUpdates = false,
    onPriceChange,
    historyPointCount,
    preferStoredQuotes = false,
  } // Destructure currencyPair from props
) => {
  const theme = useTheme();
  const { timeZone } = useDisplayTimeZone();
  const [chartData, setChartData] = useState<FastForexPricePoint[]>([]);
  const [fetchError, setFetchError] = useState(false);
  const [loadedHistoryKey, setLoadedHistoryKey] = useState("");
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const onPriceChangeRef = useRef(onPriceChange);
  const liveTailNeedsAppendRef = useRef(true);
  const historyKey = `${currencyPair}:${executionTimestamp || "live"}:${priceWindowMs}:${historyPointCount || "auto"}`;
  const chartOptions = useMemo(
    () =>
      ChartOptions({
        prices: chartData,
        selectedSymbol: currencyPair,
        showBidAsk,
        chartHeight,
        theme,
        timeZone,
      }),
    [chartData, chartHeight, currencyPair, showBidAsk, theme, timeZone]
  );

  useEffect(() => {
    onPriceChangeRef.current = onPriceChange;
  }, [onPriceChange]);

  useEffect(() => {
    // A Highcharts options update (for example, resizing the tile) rebuilds
    // the series from React-managed history, so the next tick must add a new
    // live tail rather than updating the last historical point.
    liveTailNeedsAppendRef.current = true;
  }, [chartHeight, currencyPair, showBidAsk]);

  useLayoutEffect(() => {
    // History is intentionally committed only once per 60 raw ticks. When
    // that state commits, Highcharts receives the frozen point but not the
    // next group's mutable tail. A display time zone change remounts the
    // chart from history, so it also needs a fresh tail before the next frame.
    liveTailNeedsAppendRef.current = true;
  }, [chartData, timeZone]);

  useEffect(() => {
    let active = true;
    setLoadedHistoryKey("");
    setChartData([]);
    setFetchError(false);

    if (!currencyPair) {
      setLoadedHistoryKey(historyKey);
      return;
    }

    const parsedExecutionTimestamp = executionTimestamp
      ? parseUtcTimestampToMs(executionTimestamp)
      : Date.now();
    if (!Number.isFinite(parsedExecutionTimestamp)) {
      setFetchError(true);
      setLoadedHistoryKey(historyKey);
      return;
    }

    const abortController = new AbortController();
    setFetchError(false);

    fetchFastForexPrices(
      currencyPair,
      parsedExecutionTimestamp,
      priceWindowMs,
      abortController.signal,
      historyPointCount,
      preferStoredQuotes
    )
      .then((prices) => {
        if (!active) return;
        setChartData(prices);
        const currentPrice = prices[prices.length - 1]?.current_price;
        if (currentPrice != null) {
          onPriceChangeRef.current?.(currentPrice);
        }
      })
      .catch((error) => {
        if (active && error?.name !== "AbortError") {
          setChartData([]);
          setFetchError(true);
        }
      })
      .finally(() => {
        if (active) {
          setLoadedHistoryKey(historyKey);
        }
      });

    return () => {
      active = false;
      abortController.abort();
    };
  }, [
    currencyPair,
    executionTimestamp,
    historyKey,
    historyPointCount,
    preferStoredQuotes,
    priceWindowMs,
  ]);

  useEffect(() => {
    if (
      !enableLiveUpdates ||
      !currencyPair ||
      loadedHistoryKey !== historyKey
    ) {
      return undefined;
    }

    const expectedSymbol = normaliseFastForexSymbol(currencyPair);
    let socket: WebSocket | null = null;
    let reconnectTimer: number | undefined;
    let reconnectAttempts = 0;
    let animationFrame: number | undefined;
    let liveTickCount = 0;
    let latestLivePrice: FastForexPricePoint | null = null;
    let disposed = false;

    const renderLatestLivePrice = () => {
      animationFrame = undefined;
      if (!latestLivePrice || disposed) return;

      const chart = chartRef.current?.chart;

      if (
        updateLiveChartPoint(
          chart,
          latestLivePrice,
          liveTailNeedsAppendRef.current,
          showBidAsk
        )
      ) {
        liveTailNeedsAppendRef.current = false;
      }

      // Keep consumers current without forcing a React/Highcharts update for
      // every raw websocket message.
      onPriceChangeRef.current?.(latestLivePrice.current_price);
    };

    const scheduleLiveChartUpdate = () => {
      if (animationFrame != null) return;
      animationFrame = window.requestAnimationFrame(renderLatestLivePrice);
    };

    const scheduleReconnect = () => {
      if (disposed) return;

      const delay = Math.min(10000, 1000 * 2 ** reconnectAttempts);
      reconnectAttempts += 1;
      reconnectTimer = window.setTimeout(connect, delay);
    };

    const connect = async () => {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) {
          throw new Error("Missing user ID for FastForex websocket auth");
        }

        const wsUrl = await getAuthenticatedWebSocketUrl(
          `PRICE:${currencyPair}`,
          userId,
          undefined,
          "fastforex"
        );
        if (disposed) return;

        socket = new WebSocket(wsUrl);

        socket.onopen = () => {
          reconnectAttempts = 0;
        };

        socket.onmessage = ({ data }) => {
          try {
            const tick = JSON.parse(data);
            if (
              tick?.source !== "fastforex" ||
              normaliseFastForexSymbol(tick?.symbol) !== expectedSymbol
            ) {
              return;
            }

            const nextPrice = normaliseFastForexLiveTick(tick);
            if (!nextPrice) return;

            if (liveTickCount === 0) {
              liveTailNeedsAppendRef.current = true;
            }
            latestLivePrice = nextPrice;
            liveTickCount += 1;
            scheduleLiveChartUpdate();

            if (liveTickCount === LIVE_TICKS_PER_HISTORY_POINT) {
              // The live tail stays visually current. Only every 60 ticks do
              // we commit one value into React-managed chart history.
              liveTickCount = 0;
              setChartData((prices) =>
                addFastForexHistoryPoint(prices, nextPrice)
              );
            }
          } catch (error) {
            console.error("[FastForexPriceChart] WS parse error:", error);
          }
        };

        socket.onerror = (error) =>
          console.error("[FastForexPriceChart] WS error:", error);
        socket.onclose = scheduleReconnect;
      } catch (error) {
        console.error("[FastForexPriceChart] WS connection error:", error);
        scheduleReconnect();
      }
    };

    void connect();

    return () => {
      disposed = true;
      if (reconnectTimer != null) {
        window.clearTimeout(reconnectTimer);
      }
      if (animationFrame != null) {
        window.cancelAnimationFrame(animationFrame);
      }
      socket?.close();
    };
  }, [
    currencyPair,
    enableLiveUpdates,
    historyKey,
    loadedHistoryKey,
    showBidAsk,
  ]);

  const useStyles = makeStyles({
    container: {
      width: "100%",
      height: "100%",
      // padding: "10px",
    },
    chartContainer: {
      overflow: "auto",
      height: "100%",
      width: "100%",
      backgroundColor: theme.globallink.colors.Grey[1200],
      // padding: "10px",
    },
    errorMessage: {
      alignItems: "center",
      color: theme.globallink.colors.Grey[500],
      display: "flex",
      height: "100%",
      justifyContent: "center",
      padding: "12px",
      textAlign: "center",
    },
  });

  const classes = useStyles();

  return (
    <>
      <div className={classes.container}>
        <div className={classes.chartContainer}>
          {fetchError ? (
            <div className={classes.errorMessage} role="status">
              Price history is unavailable for this execution window.
            </div>
          ) : (
            <HighchartsReact
              // Highcharts 11 ignores `time` changes on update; remount so
              // ticks move to the newly selected zone.
              key={timeZone}
              ref={chartRef}
              highcharts={Highcharts}
              options={chartOptions}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default FastForexPriceChartTileComponent;

export const formattedPrices = (prices: any) => {
  return prices.map((item: any) => {
    return [
      parseUtcTimestampToMs(item.timestamp),
      parseFloat(item.current_price), // Price as a number
    ];
  });
};

export const formattedSpotAsk = (prices: any) => {
  return prices.map((item: any) => [
    parseUtcTimestampToMs(item.timestamp),
    parseFloat(item.spot_ask),
  ]);
};

export const formattedSpotBid = (prices: any) => {
  return prices.map((item: any) => [
    parseUtcTimestampToMs(item.timestamp),
    parseFloat(item.spot_bid),
  ]);
};

export const ChartOptions = ({
  prices,
  selectedSymbol,
  showBidAsk = true,
  chartHeight = "200px",
  theme,
  timeZone,
}: any) => {
  void selectedSymbol;

  // Label the axis with the zone's name at the latest point (EST vs EDT).
  const axisZoneLabel = getTimeZoneSuffix(
    timeZone,
    parseUtcTimestampToMs(prices?.[prices.length - 1]?.timestamp)
  );

  const safeMidNums = (prices || [])
    .map((x: any) => parseFloat(x?.current_price))
    .filter((n: number) => Number.isFinite(n));

  const safeBidNums = (prices || [])
    .map((x: any) => parseFloat(x?.spot_bid))
    .filter((n: number) => Number.isFinite(n));

  const safeAskNums = (prices || [])
    .map((x: any) => parseFloat(x?.spot_ask))
    .filter((n: number) => Number.isFinite(n));

  const yAxisValues = showBidAsk
    ? [...safeBidNums, ...safeMidNums, ...safeAskNums]
    : safeMidNums;

  const yMin =
    yAxisValues.length > 0 ? Math.min(...yAxisValues) : undefined;

  const yMax =
    yAxisValues.length > 0 ? Math.max(...yAxisValues) : undefined;

  const midSeries = {
    type: "line",
    name: showBidAsk ? "Mid" : "Spot Mid",
    data: formattedPrices(prices),
    yAxis: 0,
    color: "#a3ff7a",
    marker: { enabled: true, radius: 1 },
  };

  const series = showBidAsk
    ? [
        {
          type: "line",
          name: "Bid",
          data: formattedSpotBid(prices),
          yAxis: 0,
          color: "#ff6b6b",
          marker: { enabled: true, radius: 1 },
        },
        midSeries,
        {
          type: "line",
          name: "Ask",
          data: formattedSpotAsk(prices),
          yAxis: 0,
          color: "#4dabf7",
          marker: { enabled: true, radius: 1 },
        },
      ]
    : [midSeries];

  return {
    // Tick positions follow the display zone's wall clock, not only labels.
    time: getHighchartsTimeOptions(timeZone),
    chart: {
      type: "line",
      height: chartHeight,
      zoomType: "x",
      backgroundColor: theme.globallink.colors.Grey[1200],
    },
    title: { text: `` },
    xAxis: {
      type: "datetime",
      title: { text: `Time (${axisZoneLabel})` },
      labels: {
        style: { color: theme.globallink.colors.Grey[500] },
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
          return formatChartAxisTime(Number(this.value), timeZone);
        },
      },
      gridLineWidth: 0,
    },
    yAxis: [
      {
        title: { text: "Price" },
        opposite: false,
        softMin: yMin,
        softMax: yMax,
        gridLineWidth: 0,
        labels: { style: { color: theme.globallink.colors.Grey[500] } },
      },
    ],
    legend: {
      enabled: showBidAsk,
      itemStyle: {
        color: theme.globallink.colors.Grey[500],
        fontWeight: "normal",
      },
      itemHoverStyle: { color: "#FFFFFF" },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      style: {
        color: theme.globallink.colors.Grey[500],
      },
      backgroundColor: theme.globallink.colors.DarkGrey[500],
      formatter: function (this: Highcharts.TooltipFormatterContextObject) {
        const index = this.points ? this.points[0].point.index : 0;
        const livePointValues = new Map(
          (this.points || []).map((point) => [point.series.name, point.y])
        );

        const bid = Number(
          livePointValues.get("Bid") ?? prices?.[index]?.spot_bid
        );
        const ask = Number(
          livePointValues.get("Ask") ?? prices?.[index]?.spot_ask
        );
        const rawMid = Number(
          livePointValues.get(showBidAsk ? "Mid" : "Spot Mid") ??
            prices?.[index]?.current_price);
        const mid =
          Number.isFinite(rawMid)
            ? rawMid
            : Number.isFinite(bid) && Number.isFinite(ask)
              ? (bid + ask) / 2
              : NaN;

        const time = formatShortTimeWithZone(
          this.points?.[0]?.x ?? prices?.[index]?.timestamp,
          timeZone
        );

        if (!showBidAsk) {
          return `
            <b>${time}</b><br/>
            <span style="color:#a3ff7a">●</span>Spot Mid: <b>${Number.isFinite(mid) ? mid.toFixed(4) : "-"}</b><br/>
          `;
        }

        return `
          <b>${time}</b><br/>
          <span style="color:#dc3545">●</span> Spot Bid: <b>${Number.isFinite(bid) ? bid.toFixed(4) : "-"}</b><br/>
          <span style="color:#007bff">●</span> Spot Mid: <b>${Number.isFinite(mid) ? mid.toFixed(4) : "-"}</b><br/>
          <span style="color:#28a745">●</span> Spot Ask: <b>${Number.isFinite(ask) ? ask.toFixed(4) : "-"}</b><br/>
        `;
      },
    },
    plotOptions: {
      series: {
        marker: { enabled: true, radius: 1 },
      },
    },

    series,

    credits: { enabled: false },
  };
};
